import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { cn } from '@workspace/ui/lib/utils';

import type { CloudflareResizeOptions } from '~/components/CloudflareResponsiveImage/CloudflareResizeOptions';
import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import type { SrcSetAnchor } from '~/components/CloudflareResponsiveImage/types';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { useGlobal } from '~/hooks/useGlobal';
import type { ShopCategories } from '~/stores/CategoriesStore';

import {
  ProductCarouselDesktop,
  ProductCarouselMobile,
} from '../isolated/ProductCarousel';
import { createCategoryUrl } from './utils';

/** Rendered tile sizes, mirroring `size-22` / `md:size-55` below. */
const MOBILE_TILE_SIZE = 88;
const DESKTOP_TILE_SIZE = 220;

/**
 * Candidates and layout hint for the tile image.
 *
 * The width is picked by the browser from `sizes`, not by the JS `isMobile`
 * flag: `useGlobal().isMobile` is `max-width: 1024px` and starts from a
 * server-side UA guess, while the tile switches size at Tailwind's `md`
 * (768px). Deciding in JS therefore both under-requested the 768-1024px range
 * and could swap `src` after hydration, re-downloading above-the-fold images.
 */
const TILE_SRCSET_PARAMS: Record<SrcSetAnchor, CloudflareResizeOptions> = {
  '88w': { fit: 'contain', width: MOBILE_TILE_SIZE },
  '176w': { fit: 'contain', width: MOBILE_TILE_SIZE * 2 },
  '220w': { fit: 'contain', width: DESKTOP_TILE_SIZE },
  '440w': { fit: 'contain', width: DESKTOP_TILE_SIZE * 2 },
};
const TILE_SIZES = `(max-width: 767px) ${MOBILE_TILE_SIZE}px, ${DESKTOP_TILE_SIZE}px`;

const containerClass = 'mx-auto w-full max-w-(--layout-max-width) max-md:px-4';
const slideClass =
  'basis-[88px]! shrink-0 grow-0 pl-0! [&:not(:last-child)]:mr-2 md:[&:not(:last-child)]:mr-4 [&>div]:w-full md:basis-[220px]! group';
const itemClass = 'w-[88px] shrink-0 md:w-[220px] group';
const carouselContentClass = 'pb-2 overscreen-4!';

interface ShopCategoriesCarouselProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  readonly shopCategories: ShopCategories;
}

const ShopCategoriesCarousel: React.FC<ShopCategoriesCarouselProps> = ({
  shopCategories: { list: categories },
  className,
  ...props
}) => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();
  const { handleClickCategory } = useCategoryAnalytics();

  if (!categories.length || !categories[0]?.id) {
    return null;
  }

  const commonProps = {
    title: t('header:categories'),
    items: categories
      .filter(({ id }) => typeof id !== 'string' || !id.startsWith('temp-'))
      .map(({ name, previewImage, id, previewText }) => ({
        id,
        node: (
          <Link
            key={id}
            data-link={`category-${id}`}
            onClick={handleClickCategory(id)}
            to={createCategoryUrl({ id })}
          >
            <div>
              {previewImage ? (
                <CloudflareResponseImage
                  src={previewImage}
                  srcSetParams={TILE_SRCSET_PARAMS}
                  sizes={TILE_SIZES}
                  width={DESKTOP_TILE_SIZE}
                  height={DESKTOP_TILE_SIZE}
                  alt={name}
                  className={cn(
                    'outline! size-22 rounded-5 align-middle outline-stroke-tertiary! -outline-offset-1 md:size-55 md:rounded-6.5',
                    'group-nth-[4n+1]:bg-[linear-gradient(transparent,#ff8aaf)]',
                    'group-nth-[4n+2]:bg-[linear-gradient(transparent,#deb6b9)]',
                    'group-nth-[4n+3]:bg-[linear-gradient(transparent,#77b2d4)]',
                    'group-nth-[4n+4]:bg-[linear-gradient(transparent,#8587cd)]',
                  )}
                />
              ) : (
                <div className="size-22 rounded-3 border-0 align-middle md:size-55" />
              )}
              <div
                className={cn(
                  'mt-3 mb-[3px] line-clamp-3 text-center text-(--text900Prim) [font:var(--headingH3)] max-md:[font:var(--text-100-medium)]',
                )}
              >
                {previewText || name}
              </div>
            </div>
          </Link>
        ),
      })),
  };

  return (
    <div {...props} className={cn(containerClass, className)}>
      {isMobile ? (
        <ProductCarouselMobile {...commonProps} itemClassName={itemClass} />
      ) : (
        <ProductCarouselDesktop
          {...commonProps}
          navigationVariant="header"
          slideClassName={slideClass}
          contentClassName={carouselContentClass}
        />
      )}
    </div>
  );
};

export default observer(ShopCategoriesCarousel);
